import { forwardRef } from 'react';
import Tooltip, { tooltipPositions } from './Tooltip';

interface BaseProps {
  active?: boolean;
  loading?: boolean;
  variant?:
    | 'primary-hover'
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'danger-hover'
    | 'success'
    | 'success-hover'
    | 'custom';
  Icon?: React.ElementType;
  iconProps?: React.SVGProps<SVGAElement>;
  small?: boolean;
  padding?: boolean;
  circle?: boolean;
  rounded?: boolean;
  error?: boolean;
  tooltip?: { text: string; position?: tooltipPositions };
  iconPosition?: 'left' | 'right';
}

interface RealButtonProps extends BaseProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  mock?: false;
}

interface MockButtonProps extends BaseProps, React.HTMLAttributes<HTMLDivElement> {
  mock: true;
}

export type ButtonProps = RealButtonProps | MockButtonProps;

const Element = forwardRef(function mockElement({ children, mock, ...props }: ButtonProps, ref) {
  if (mock)
    return (
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        {...(props as React.HTMLAttributes<HTMLDivElement>)}
      >
        {children}
      </div>
    );
  return (
    <button
      ref={ref as React.RefObject<HTMLButtonElement>}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  );
});

const Button = ({
  children,
  variant = 'primary-hover',
  className,
  tooltip,
  Icon,
  iconProps,
  small,
  circle,
  error,
  padding = true,
  rounded = true,
  active = false,
  loading = false,
  iconPosition = 'left',
  ...props
}: ButtonProps) => {
  const primaryClasses = ['bg-blue-500', 'text-white', 'shadow-sm', 'hover:bg-blue-500/90'];

  const secondaryClasses = [
    'bg-gray-200',
    'dark:bg-gray-800',
    'shadow-sm',
    'hover:bg-gray-200/80',
    'dark:hover:bg-gray-800/80'
  ];

  const dangerClasses = [
    'bg-red-500',
    'text-white',
    'shadow-sm',
    'hover:bg-red-500/90',
    'dark:bg-red-800/60',
    'dark:hover:bg-red-800/50',
    'focus-visible:ring-red-500/20',
    'dark:focus-visible:ring-red-800/40'
  ];

  const primaryHoverClasses = [
    'hover:text-white',
    'hover:bg-blue-500',
    'hover:shadow-sm',
    'dark:hover:bg-blue-500',
    'data-[active=true]:text-white',
    'data-[active=true]:bg-blue-500',
    'data-[active=true]:shadow-none'
  ];

  const dangerHoverClasses = [
    'hover:bg-red-500',
    'dark:hover:bg-red-800/60',
    'hover:text-white',
    'hover:shadow-sm',
    'data-[active=true]:bg-red-500',
    'dark:data-[active=true]:bg-red-800/60',
    'data-[active=true]:text-white',
    'data-[active=true]:shadow-none',
    'focus-visible:ring-red-500/20',
    'dark:focus-visible:ring-red-800/40'
  ];

  const successClasses = [
    'bg-green-700',
    'text-white',
    'shadow-sm',
    'hover:bg-green-700/90',
    'dark:bg-green-800',
    'dark:hover:bg-green-800/90',
    'focus-visible:ring-green-700/20',
    'dark:focus-visible:ring-green-800/40'
  ];

  const successHoverClasses = [
    'hover:bg-green-700',
    'dark:hover:bg-green-800',
    'hover:text-white',
    'hover:shadow-sm',
    'data-[active=true]:bg-green-700',
    'dark:data-[active=true]:bg-green-800',
    'data-[active=true]:text-white',
    'data-[active=true]:shadow-none',
    'focus-visible:ring-green-700/20',
    'dark:focus-visible:ring-green-800/40'
  ];

  const variants = {
    custom: '',
    primary: primaryClasses,
    secondary: secondaryClasses,
    danger: dangerClasses,
    success: successClasses,
    'primary-hover': primaryHoverClasses,
    'danger-hover': dangerHoverClasses,
    'success-hover': successHoverClasses
  };

  const baseClasses = [
    'cursor-pointer',
    'focus-visible:border-ring',
    'flex',
    'shrink-0',
    'items-center',
    'justify-center',
    'gap-2',
    padding && !small && 'p-2',
    small && 'p-1',
    rounded && !circle && 'rounded-lg',
    circle && 'rounded-full',
    'whitespace-nowrap',
    'transition-all',
    'outline-none',
    'focus-visible:ring-2',
    'disabled:opacity-50',
    'aria-invalid:bg-red-500',
    'aria-invalid:text-white',
    'dark:aria-invalid:bg-red-500/60',
    '[&_svg]:pointer-events-none',
    '[&_svg]:shrink-0',
    ...(variant && variants[variant]),
    className
  ]
    .filter((d) => d)
    .join(' ');

  const { mock } = props;

  return (
    <Tooltip {...(tooltip && tooltip)} isActive={!!tooltip}>
      {mock ? (
        <Element
          className={baseClasses}
          aria-invalid={error}
          data-active={active ? 'true' : 'false'}
          {...props}
        >
          {loading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-4 border-blue-500 border-t-transparent" />
          ) : (
            Icon && iconPosition !== 'right' && <Icon {...iconProps} />
          )}
          {children}
          {Icon && iconPosition === 'right' && <Icon {...iconProps} />}
        </Element>
      ) : (
        <Element
          mock={false}
          disabled={error || props.disabled || loading}
          className={baseClasses}
          aria-invalid={error}
          data-active={active ? 'true' : 'false'}
          {...props}
        >
          {loading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-4 border-blue-500 border-t-transparent" />
          ) : (
            Icon && iconPosition !== 'right' && <Icon {...iconProps} />
          )}
          {children}
          {Icon && iconPosition === 'right' && <Icon {...iconProps} />}
        </Element>
      )}
    </Tooltip>
  );
};

export default Button;
